// ═══════════════════════════════════════════════════════════════
// ClearView CRM Persona Settings — Admin UI for Managing Personas
// Location: src/components/settings/PersonaSettings.jsx
//
// READABILITY PATTERN RULES (apply to all ClearView CRM components):
//
// 1. TEXT: Always use palette.text1 or palette.text2 for ANY text
//    the user needs to read. Never use palette.text3, palette.accent,
//    or any color lighter than #374151 for readable content.
//
// 2. BACKGROUNDS: Any surface that contains text must use a SOLID
//    background — either palette.cardBg or "#ffffff". Never put
//    readable text on a semi-transparent or tinted surface without
//    ensuring the text is palette.text1 (near-black).
//
// 3. BUTTONS: Text on buttons must be either "#ffffff" (on solid
//    colored backgrounds) or palette.text1 (on light backgrounds).
//    Never use palette.accent as text color on light backgrounds —
//    many accent colors are too light to read.
//
// 4. BADGES/PILLS: Use solid background with white text, OR
//    use palette.text1 text with a subtle background. Never rely
//    on colored text alone for readability.
//
// 5. ICONS: Functional icons (edit, delete, nav) must be at least
//    palette.text2 (#1f2937). Decorative icons can use accent.
//
// 6. LABELS: Form labels and section headers use palette.text1
//    with fontWeight 600+. Never use uppercase + light color —
//    pick one emphasis method, not both.
// ═══════════════════════════════════════════════════════════════
import React, { useState, useMemo } from "react";
import {
  Plus,
  Edit3,
  Trash2,
  Save,
  X,
  User,
  Users,
  BookOpen,
  Shield,
  TrendingUp,
  Heart,
  Star,
  Target,
  Compass,
  Lightbulb,
  Award,
  Zap,
} from "lucide-react";
import { useApp } from "../../context/AppContext";
import { FONT_SIZES, FONT_FAMILY, ICON_SIZES } from "../../constants/appConfig";
import { PATHWAY_COLORS } from "../../constants/pathwayDefaults";

// ── Icon Options ────────────────────────────────────────────
const ICON_OPTIONS = [
  { id: "User", icon: User, label: "Person" },
  { id: "Users", icon: Users, label: "Community" },
  { id: "BookOpen", icon: BookOpen, label: "Learning" },
  { id: "Shield", icon: Shield, label: "Integrity" },
  { id: "TrendingUp", icon: TrendingUp, label: "Growth" },
  { id: "Heart", icon: Heart, label: "Passion" },
  { id: "Star", icon: Star, label: "Excellence" },
  { id: "Target", icon: Target, label: "Focus" },
  { id: "Compass", icon: Compass, label: "Direction" },
  { id: "Lightbulb", icon: Lightbulb, label: "Ideas" },
  { id: "Award", icon: Award, label: "Achievement" },
  { id: "Zap", icon: Zap, label: "Energy" },
];

const getIconComponent = (iconId) => {
  const found = ICON_OPTIONS.find((o) => o.id === iconId);
  return found ? found.icon : User;
};

// ── Slug Generator ──────────────────────────────────────────
const toSlug = (name) =>
  name
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .substring(0, 40);

// ── Main Component ──────────────────────────────────────────
export const PersonaSettings = () => {
  const {
    palette,
    personas,
    members,
    addPersona,
    updatePersona,
    removePersona,
    isAdmin,
  } = useApp();

  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    actionHint: "",
    color: "#3b82f6",
    icon: "User",
  });
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  // Count members per persona
  const memberCounts = useMemo(() => {
    const counts = {};
    members.forEach((m) => {
      if (m.persona) {
        counts[m.persona] = (counts[m.persona] || 0) + 1;
      }
    });
    return counts;
  }, [members]);

  const unassignedCount = useMemo(
    () => members.filter((m) => !m.persona).length,
    [members]
  );

  // ── Form Handlers ───────────────────────────────────────
  const startAdd = () => {
    setFormData({
      name: "",
      description: "",
      actionHint: "",
      color: "#3b82f6",
      icon: "User",
    });
    setEditingId("new");
    setDeleteConfirm(null);
  };

  const startEdit = (persona) => {
    setFormData({
      name: persona.name,
      description: persona.description,
      actionHint: persona.actionHint,
      color: persona.color,
      icon: persona.icon || "User",
    });
    setEditingId(persona.id);
    setDeleteConfirm(null);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setFormData({
      name: "",
      description: "",
      actionHint: "",
      color: "#3b82f6",
      icon: "User",
    });
  };

  const handleSave = async () => {
    if (!formData.name.trim()) return;

    if (editingId === "new") {
      const slug = toSlug(formData.name);
      if (personas.find((p) => p.id === slug)) {
        alert(
          `A persona with the ID "${slug}" already exists. Please choose a different name.`
        );
        return;
      }
      await addPersona({
        id: slug,
        name: formData.name.trim(),
        description: formData.description.trim(),
        actionHint: formData.actionHint.trim(),
        color: formData.color,
        icon: formData.icon,
        sortOrder: personas.length,
      });
    } else {
      await updatePersona(editingId, {
        name: formData.name.trim(),
        description: formData.description.trim(),
        actionHint: formData.actionHint.trim(),
        color: formData.color,
        icon: formData.icon,
      });
    }
    cancelEdit();
  };

  const handleDelete = async (id) => {
    await removePersona(id);
    setDeleteConfirm(null);
    if (editingId === id) cancelEdit();
  };

  // ── Styles ──────────────────────────────────────────────
  // PATTERN RULE: All text uses palette.text1 or palette.text2.
  // Backgrounds are solid white or palette.cardBg.
  // Buttons use solid accent bg + white text, never accent text on light bg.
  const s = {
    container: {
      padding: "0 4px",
    },
    header: {
      marginBottom: 20,
    },
    title: {
      fontSize: FONT_SIZES.lg,
      fontFamily: FONT_FAMILY,
      fontWeight: 700,
      color: palette.text1,
      margin: 0,
    },
    subtitle: {
      fontSize: FONT_SIZES.sm,
      fontFamily: FONT_FAMILY,
      color: palette.text2, // RULE 1: readable text = text1 or text2
      marginTop: 4,
      lineHeight: 1.5,
    },
    // ── Add Button: solid accent background, white text ──
    // RULE 3: Never use accent as text color on light background
    addBtn: {
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      padding: "8px 16px",
      borderRadius: 8,
      border: "none",
      background: palette.accent || palette.accent1,
      color: "#ffffff",
      fontSize: FONT_SIZES.sm,
      fontFamily: FONT_FAMILY,
      fontWeight: 600,
      cursor: "pointer",
      marginTop: 12,
    },
    // ── Persona Card: solid white bg, dark left border ──
    // RULE 2: solid background for any surface with text
    card: (color) => ({
      background: palette.cardBg,
      borderRadius: 12,
      border: `1px solid ${palette.cardBorder}`,
      borderLeft: `4px solid ${color}`,
      padding: 16,
      marginBottom: 12,
    }),
    cardHeader: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      marginBottom: 8,
    },
    cardName: {
      display: "flex",
      alignItems: "center",
      gap: 8,
    },
    nameText: {
      fontSize: FONT_SIZES.md,
      fontFamily: FONT_FAMILY,
      fontWeight: 700,
      color: palette.text1, // RULE 1: near-black for headings
    },
    // ── Count Badge: solid colored background, white text ──
    // RULE 4: solid bg + white text, never colored text alone
    countBadge: (color) => ({
      fontSize: FONT_SIZES.xs,
      fontFamily: FONT_FAMILY,
      padding: "2px 10px",
      borderRadius: 10,
      background: color,
      color: "#ffffff",
      fontWeight: 600,
    }),
    // ── Edit/Delete icons: dark enough to see ──
    // RULE 5: functional icons use text2 minimum
    cardActions: {
      display: "flex",
      gap: 4,
    },
    iconBtn: {
      background: "transparent",
      border: "none",
      cursor: "pointer",
      padding: 6,
      borderRadius: 6,
      color: palette.text2, // RULE 5: functional icons = text2
      display: "flex",
      alignItems: "center",
    },
    // ── Section Labels: bold, near-black, normal case ──
    // RULE 6: dark + bold, not uppercase + light
    sectionLabel: {
      fontSize: FONT_SIZES.xs,
      fontFamily: FONT_FAMILY,
      fontWeight: 700,
      color: palette.text1, // RULE 6: labels = text1 + bold
      letterSpacing: "0.03em",
      marginBottom: 4,
      marginTop: 12,
      textTransform: "uppercase",
    },
    descText: {
      fontSize: FONT_SIZES.sm,
      fontFamily: FONT_FAMILY,
      color: palette.text2, // RULE 1: body text = text2
      lineHeight: 1.6,
    },
    // ── Action Hint Box: solid white bg with colored left border ──
    // RULE 2: solid background under readable text
    hintBox: (color) => ({
      background: palette.cardBg,
      borderLeft: `3px solid ${color}`,
      borderRadius: "0 8px 8px 0",
      padding: "10px 14px",
      marginTop: 6,
      border: `1px solid ${palette.cardBorder}`,
      borderLeftWidth: 3,
      borderLeftColor: color,
    }),
    hintText: {
      fontSize: FONT_SIZES.sm,
      fontFamily: FONT_FAMILY,
      color: palette.text2, // RULE 1: readable text, not gray italic
      lineHeight: 1.6,
      fontStyle: "italic",
    },
    // ── Form ──
    formContainer: {
      background: palette.cardBg, // RULE 2: solid background
      borderRadius: 12,
      border: `1px solid ${palette.cardBorder}`,
      padding: 20,
      marginBottom: 16,
    },
    formTitle: {
      fontSize: FONT_SIZES.md,
      fontFamily: FONT_FAMILY,
      fontWeight: 700,
      color: palette.text1,
      marginBottom: 16,
    },
    fieldGroup: {
      marginBottom: 14,
    },
    fieldLabel: {
      display: "block",
      fontSize: FONT_SIZES.sm,
      fontFamily: FONT_FAMILY,
      fontWeight: 600,
      color: palette.text1, // RULE 6: labels = text1 + bold
      marginBottom: 4,
    },
    fieldHint: {
      fontSize: FONT_SIZES.xs,
      fontFamily: FONT_FAMILY,
      color: palette.text2, // RULE 1: helper text still readable
      marginBottom: 4,
    },
    input: {
      width: "100%",
      padding: "8px 12px",
      borderRadius: 8,
      border: `1px solid ${palette.inputBorder}`,
      background: palette.inputBg,
      color: palette.inputColor,
      fontSize: FONT_SIZES.sm,
      fontFamily: FONT_FAMILY,
      boxSizing: "border-box",
    },
    textarea: {
      width: "100%",
      padding: "8px 12px",
      borderRadius: 8,
      border: `1px solid ${palette.inputBorder}`,
      background: palette.inputBg,
      color: palette.inputColor,
      fontSize: FONT_SIZES.sm,
      fontFamily: FONT_FAMILY,
      lineHeight: 1.5,
      resize: "vertical",
      minHeight: 80,
      boxSizing: "border-box",
    },
    colorGrid: {
      display: "flex",
      flexWrap: "wrap",
      gap: 6,
      marginTop: 4,
    },
    colorSwatch: (color, isSelected) => ({
      width: 28,
      height: 28,
      borderRadius: 6,
      background: color,
      border: isSelected
        ? `3px solid ${palette.text1}`
        : "2px solid transparent",
      cursor: "pointer",
      transition: "transform 0.1s",
      transform: isSelected ? "scale(1.15)" : "scale(1)",
    }),
    iconGrid: {
      display: "flex",
      flexWrap: "wrap",
      gap: 4,
      marginTop: 4,
    },
    iconOption: (isSelected) => ({
      width: 36,
      height: 36,
      borderRadius: 8,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      background: isSelected ? palette.accent || palette.accent1 : palette.cardBg,
      border: isSelected
        ? `2px solid ${palette.accent || palette.accent1}`
        : `1px solid ${palette.cardBorder}`,
      cursor: "pointer",
      color: isSelected ? "#ffffff" : palette.text2, // RULE 3: white on accent bg
    }),
    formButtons: {
      display: "flex",
      gap: 8,
      marginTop: 16,
    },
    saveBtn: {
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      padding: "8px 20px",
      borderRadius: 8,
      border: "none",
      background: palette.accent || palette.accent1,
      color: "#ffffff", // RULE 3: white text on colored button
      fontSize: FONT_SIZES.sm,
      fontFamily: FONT_FAMILY,
      fontWeight: 600,
      cursor: "pointer",
    },
    cancelBtn: {
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      padding: "8px 16px",
      borderRadius: 8,
      border: `1px solid ${palette.cardBorder}`,
      background: palette.cardBg,
      color: palette.text1, // RULE 1: dark text on light button
      fontSize: FONT_SIZES.sm,
      fontFamily: FONT_FAMILY,
      cursor: "pointer",
    },
    deleteConfirm: {
      background: "#fef2f2",
      border: "1px solid #fca5a5",
      borderRadius: 8,
      padding: "10px 14px",
      marginTop: 8,
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 12,
    },
    deleteText: {
      fontSize: FONT_SIZES.sm,
      fontFamily: FONT_FAMILY,
      color: "#991b1b", // dark red — readable on #fef2f2
    },
    deleteBtn: {
      padding: "6px 14px",
      borderRadius: 6,
      border: "none",
      background: "#dc2626",
      color: "#ffffff", // RULE 3: white on colored button
      fontSize: FONT_SIZES.sm,
      fontFamily: FONT_FAMILY,
      fontWeight: 600,
      cursor: "pointer",
      whiteSpace: "nowrap",
    },
    emptyState: {
      textAlign: "center",
      padding: "40px 20px",
    },
    emptyTitle: {
      fontSize: FONT_SIZES.md,
      fontFamily: FONT_FAMILY,
      fontWeight: 600,
      color: palette.text1,
      marginBottom: 8,
    },
    emptyText: {
      fontSize: FONT_SIZES.sm,
      fontFamily: FONT_FAMILY,
      color: palette.text2, // RULE 1
      lineHeight: 1.6,
      maxWidth: 480,
      margin: "0 auto",
    },
    // ── Stats Bar: solid white background ──
    // RULE 2: solid bg under readable content
    statsBar: {
      display: "flex",
      gap: 12,
      flexWrap: "wrap",
      marginBottom: 16,
      padding: "10px 14px",
      background: palette.cardBg,
      borderRadius: 10,
      border: `1px solid ${palette.cardBorder}`,
    },
    statPill: {
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      fontSize: FONT_SIZES.sm,
      fontFamily: FONT_FAMILY,
      color: palette.text1, // RULE 1: near-black stat text
      fontWeight: 500,
    },
    statDot: (color) => ({
      width: 10,
      height: 10,
      borderRadius: "50%",
      background: color,
      flexShrink: 0,
    }),
  };

  // ── Render Form ─────────────────────────────────────────
  const renderForm = () => {
    if (!editingId) return null;
    const isNew = editingId === "new";

    return (
      <div style={s.formContainer}>
        <div style={s.formTitle}>
          {isNew ? "Add New Persona" : `Edit: ${formData.name}`}
        </div>

        {/* Name */}
        <div style={s.fieldGroup}>
          <label style={s.fieldLabel}>Persona Name</label>
          <input
            style={s.input}
            value={formData.name}
            onChange={(e) =>
              setFormData((prev) => ({ ...prev, name: e.target.value }))
            }
            placeholder='e.g., "The Practitioner"'
            maxLength={60}
          />
          {isNew && formData.name.trim() && (
            <div
              style={{
                fontSize: FONT_SIZES.xs,
                color: palette.text2,
                marginTop: 4,
                fontFamily: "monospace",
              }}
            >
              ID: {toSlug(formData.name)}
            </div>
          )}
        </div>

        {/* Description */}
        <div style={s.fieldGroup}>
          <label style={s.fieldLabel}>What does this persona authorize?</label>
          <div style={s.fieldHint}>
            Describe the value this member type seeks from the chapter.
          </div>
          <textarea
            style={s.textarea}
            value={formData.description}
            onChange={(e) =>
              setFormData((prev) => ({ ...prev, description: e.target.value }))
            }
            placeholder="Authorizes professional competence and applied learning. This member invests in the chapter because it makes them better at their job..."
          />
        </div>

        {/* Action Hint */}
        <div style={s.fieldGroup}>
          <label style={s.fieldLabel}>
            How should we cultivate this persona?
          </label>
          <div style={s.fieldHint}>
            This guidance appears on pipeline cards to help managers take the
            right next action.
          </div>
          <textarea
            style={{ ...s.textarea, minHeight: 100 }}
            value={formData.actionHint}
            onChange={(e) =>
              setFormData((prev) => ({ ...prev, actionHint: e.target.value }))
            }
            placeholder="Lead with learning. Invite to skill-focused sessions and workshops, not social events. Share relevant articles between meetings..."
          />
        </div>

        {/* Color */}
        <div style={s.fieldGroup}>
          <label style={s.fieldLabel}>Color</label>
          <div style={s.colorGrid}>
            {PATHWAY_COLORS.map((color) => (
              <div
                key={color}
                style={s.colorSwatch(color, formData.color === color)}
                onClick={() => setFormData((prev) => ({ ...prev, color }))}
                title={color}
              />
            ))}
          </div>
        </div>

        {/* Icon */}
        <div style={s.fieldGroup}>
          <label style={s.fieldLabel}>Icon</label>
          <div style={s.iconGrid}>
            {ICON_OPTIONS.map((opt) => {
              const Icon = opt.icon;
              return (
                <div
                  key={opt.id}
                  style={s.iconOption(formData.icon === opt.id)}
                  onClick={() =>
                    setFormData((prev) => ({ ...prev, icon: opt.id }))
                  }
                  title={opt.label}
                >
                  <Icon size={ICON_SIZES.sm} />
                </div>
              );
            })}
          </div>
        </div>

        {/* Buttons */}
        <div style={s.formButtons}>
          <button
            style={{
              ...s.saveBtn,
              opacity: formData.name.trim() ? 1 : 0.5,
              cursor: formData.name.trim() ? "pointer" : "not-allowed",
            }}
            onClick={handleSave}
            disabled={!formData.name.trim()}
          >
            <Save size={ICON_SIZES.sm} />
            {isNew ? "Create Persona" : "Save Changes"}
          </button>
          <button style={s.cancelBtn} onClick={cancelEdit}>
            <X size={ICON_SIZES.sm} />
            Cancel
          </button>
        </div>
      </div>
    );
  };

  // ── Render Persona Card ─────────────────────────────────
  const renderCard = (persona) => {
    const IconComp = getIconComponent(persona.icon);
    const count = memberCounts[persona.id] || 0;
    const isEditing = editingId === persona.id;

    return (
      <div key={persona.id}>
        <div style={s.card(persona.color)}>
          {/* Header */}
          <div style={s.cardHeader}>
            <div style={s.cardName}>
              <IconComp size={ICON_SIZES.md} color={persona.color} />
              <span style={s.nameText}>{persona.name}</span>
              <span style={s.countBadge(persona.color)}>
                {count} member{count !== 1 ? "s" : ""}
              </span>
            </div>
            {isAdmin && !isEditing && (
              <div style={s.cardActions}>
                <button
                  style={s.iconBtn}
                  onClick={() => startEdit(persona)}
                  title="Edit"
                >
                  <Edit3 size={ICON_SIZES.sm} />
                </button>
                <button
                  style={{ ...s.iconBtn, color: "#dc2626" }}
                  onClick={() =>
                    setDeleteConfirm(
                      deleteConfirm === persona.id ? null : persona.id
                    )
                  }
                  title="Delete"
                >
                  <Trash2 size={ICON_SIZES.sm} />
                </button>
              </div>
            )}
          </div>

          {/* Description */}
          <div style={s.sectionLabel}>What they authorize</div>
          <div style={s.descText}>{persona.description || "—"}</div>

          {/* Action Hint */}
          {persona.actionHint && (
            <>
              <div style={s.sectionLabel}>Cultivation guidance</div>
              <div style={s.hintBox(persona.color)}>
                <div style={s.hintText}>{persona.actionHint}</div>
              </div>
            </>
          )}

          {/* Delete Confirmation */}
          {deleteConfirm === persona.id && (
            <div style={s.deleteConfirm}>
              <div style={s.deleteText}>
                Delete "{persona.name}"?
                {count > 0 &&
                  ` ${count} member${
                    count !== 1 ? "s" : ""
                  } will show "No persona" until reassigned.`}
              </div>
              <button
                style={s.deleteBtn}
                onClick={() => handleDelete(persona.id)}
              >
                Delete
              </button>
              <button
                style={{
                  ...s.cancelBtn,
                  padding: "6px 12px",
                  fontSize: FONT_SIZES.xs,
                }}
                onClick={() => setDeleteConfirm(null)}
              >
                Cancel
              </button>
            </div>
          )}
        </div>

        {/* Inline edit form */}
        {isEditing && renderForm()}
      </div>
    );
  };

  // ── Main Render ─────────────────────────────────────────
  return (
    <div style={s.container}>
      {/* Header */}
      <div style={s.header}>
        <div style={s.title}>Member Personas</div>
        <div style={s.subtitle}>
          Define the authorization frameworks your members bring to the chapter.
          Each persona represents <em>why</em> a member invests in the
          relationship.
        </div>
      </div>

      {/* Stats bar */}
      {personas.length > 0 && (
        <div style={s.statsBar}>
          {personas.map((p) => (
            <div key={p.id} style={s.statPill}>
              <div style={s.statDot(p.color)} />
              {p.name}: <strong>{memberCounts[p.id] || 0}</strong>
            </div>
          ))}
          <div style={s.statPill}>
            <div style={s.statDot("#9ca3af")} />
            Unassigned: <strong>{unassignedCount}</strong>
          </div>
        </div>
      )}

      {/* Add button */}
      {isAdmin && editingId !== "new" && (
        <button style={s.addBtn} onClick={startAdd}>
          <Plus size={ICON_SIZES.sm} />
          Add Persona
        </button>
      )}

      {/* New persona form */}
      {editingId === "new" && (
        <div style={{ marginTop: 12 }}>{renderForm()}</div>
      )}

      {/* Persona list */}
      {personas.length > 0 ? (
        <div style={{ marginTop: 16 }}>
          {personas.map((p) => renderCard(p))}
        </div>
      ) : (
        editingId !== "new" && (
          <div style={s.emptyState}>
            <div style={s.emptyTitle}>No personas defined yet</div>
            <div style={s.emptyText}>
              Personas help you understand why members invest in the chapter and
              how to cultivate each relationship differently. Each persona
              captures an authorization framework — the unique reason a member
              says "yes" to this community.
            </div>
          </div>
        )
      )}
    </div>
  );
};
